/**
 * `@dxf-ai-editor/cad-core` — the AABB of the shape a heavy `POLYLINE` is
 * actually DRAWN as, swept along its group-39 thickness (@codex PR #785
 * round 9, F1 + F2).
 *
 * ONE function, because there are TWO callers and they had learned different
 * subsets of the rules:
 *
 *   - `./bounds.ts`' `"Polyline"` arm — a TOP-LEVEL heavy polyline's box, the
 *     one `getSelectGeometry` hands out beside the band points.
 *   - `./blockDefinitionWalk.ts`' `childEntityBounds` `"Polyline"` arm — the
 *     same polyline as a BLOCK-DEFINITION child, whose box is then run through
 *     the INSERT affine.
 *
 * Round 8 taught the first one about the width band and left the second on
 * width-blind `entityModelBounds`, so a width-4 child on `(0,0) → (10,0)`
 * inside an INSERT emitted select points at `y = ±2` while the INSERT's own
 * bounds stayed at `y ≡ 0` — and `blockSelectGeometry.ts`'s stated invariant
 * ("select geometry stays a strict subset of the bounds box, the safe
 * direction") was false in the UNSAFE direction: an exact-selection caller
 * using the box as its coarse candidate filter discards the entity before it
 * ever tests those points. Both arms now call THIS function, so the next rule
 * either arm learns is learned by both.
 *
 * # Why a UNION of the two boxes was not enough (F1)
 *
 * `entityModelBounds` frames the CENTRELINE and its thickness extrusion;
 * {@link heavyPolylineDrawnRuns} frames the drawn BAND in the base plane and
 * models no thickness at all. Round 8 union'd the two — but the entity a
 * DXF reader must bound is neither: group 39 SWEEPS the whole drawn shape
 * along the group-210 normal (Autodesk DXF reference, "Thickness (39)" +
 * "Object Coordinate Systems (OCS)": the extrusion depth is applied along the
 * entity's extrusion direction, which for a 2D POLYLINE is its own OCS `ẑ`;
 * `ezdxf` models the same as a swept solid). The drawn entity is therefore the
 * MINKOWSKI SUM `band ⊕ [0, t·N̂]`, and a union of the two boxes is a strictly
 * smaller set whenever `N̂` is OBLIQUE — the translated band's far corner is
 * neither in the unshifted band nor on the translated centreline. Extrusion
 * `(1, 1, 1)`, thickness 5, constant width 4 on `(0,0) → (10,0)`:
 *
 *                          maxX                 maxY
 *   band                   0.8164965809277261   7.8875643927932035
 *   centreline ⊕ t·N̂       2.886751345948129    9.957819157813605
 *   union (round 8)        2.886751345948129    9.957819157813605
 *   band ⊕ t·N̂  (drawn)    3.7032479268758554   10.774315738741333
 *
 * — an under-report of `2/√6 = 0.8164965809277261` on BOTH axes, which is
 * exactly the band's own half-width projected onto the tilted frame. The fix
 * is one sentence and no new math: accumulate the drawn points AND their
 * thickness-translated copies. `[p, p + t·N̂]` bounds the whole sweep because
 * the swept set is linear in the sweep parameter — every coordinate's extreme
 * is attained at `s = 0` or `s = 1`, and a convex hull never widens an AABB.
 *
 * The translation itself is `drawing-model`'s OWN
 * {@link withThicknessAlongExtrusion} — the same call
 * `wcsBoundsLeaf.ts::heavyPolylineLocal` makes to extrude the centreline
 * (there through the OCS lift for the 2D subtype, directly for the 3D / mesh
 * subtypes, both resolving to `normalize(group 210)`). A local `normalize the
 * extrusion, then translate` would have been the second truth.
 *
 * The model box is still UNION'd in rather than replaced: it is the arm that
 * owns exact bulge extrema (this module's runs are a tessellation) and the
 * degenerate single-vertex fallback, and the rule for a bounds box is never to
 * shrink.
 *
 * # Why the walk is GATED on the authored width (round 10)
 *
 * `apps/web`'s `state/entityMetadataIndex.ts` is now a third caller, and it
 * runs per entity while building the spatial index of a 685k-entity drawing
 * (`.claude/skills/scene3d-virtualization`). Measured on this repo, 20 000
 * heavy polylines per shape, median of 5 runs:
 *
 *                                    model box only   + drawn walk
 *   no width, straight, 4 verts       0.78 us/ent      2.48 us/ent   3.2x
 *   no width, bulged,   4 verts       2.45 us/ent     10.05 us/ent   4.1x
 *   no width, straight, 20 verts      2.60 us/ent      5.37 us/ent   2.1x
 *   width 4, straight,  4 verts       0.68 us/ent      5.01 us/ent   7.4x
 *   width 4, bulged,    4 verts       2.26 us/ent     33.29 us/ent  14.7x
 *
 * For an entity that declares NO width the extra work buys NOTHING, and that
 * is a THEOREM about these two functions rather than an observation:
 * {@link heavyPolylineDrawnRuns} emits, for a width-less run, exactly
 * {@link polylineCentrelineSamples} — points that lie ON the same centreline
 * curve `entityModelBounds` bounds — and both sweep it along the SAME
 * {@link withThicknessAlongExtrusion} vector. The model box contains that
 * curve in EVERY subtype and frame:
 *
 *   - 2D, default extrusion — `wcsBoundsLeaf.ts::polylineLocalPoints` carries
 *     the authored endpoints plus `arcExtentPoints2D`' EXACT cardinal extrema
 *     of each bulge arc, and the lift is the identity, so the box bounds the
 *     arc itself, hence any point sampled on it.
 *   - 2D, tilted extrusion — that walk switches to `dense`, adding
 *     `tessellateArc2D`, whose radius is INFLATED to `r / cos(halfStep)` so its
 *     polygon CIRCUMSCRIBES the arc. A rotation preserves containment and an
 *     AABB of a convex hull is the AABB of its vertices, so the lifted box
 *     still contains the lifted arc.
 *   - 3D polyline / polygon mesh / poly-face mesh — the runs are the vertex
 *     records themselves, and the model box is the box of ALL of them (only a
 *     poly-face FACE record, which carries filler coordinates, is dropped by
 *     both).
 *
 * So the gate is not an approximation traded for speed: for a width-less
 * entity the union term is a mathematical no-op, and skipping it also removes
 * the last-ulp inflation the union could otherwise add. Confirmed empirically
 * over 4 000 generated polylines (random vertices, bulges, closed flags,
 * oblique extrusions and signed thicknesses): the worst widening the drawn
 * runs contributed to the model box was 2.8e-16 of the box's own size — pure
 * floating-point noise, never geometry. `./heavyPolylineDrawnBounds.property.test.ts`
 * pins that as a property (P7) rather than leaving it a one-off measurement.
 */

import {
  ALWAYS_INCLUDE,
  entityModelBounds,
  heavyPolylineDeclaresWidth,
  withThicknessAlongExtrusion,
  type PolylineEntity,
} from "@dxf-ai-editor/drawing-model";

import type { Aabb } from "./common";
import { boundsFromPoints, unionBounds } from "./common";
import { heavyPolylineDrawnRuns } from "./heavyPolylineDrawnShape";

/**
 * The WCS (XY-projected) box of a heavy `POLYLINE`: its DRAWN silhouette —
 * the width band where one is drawn, the tessellated centreline otherwise —
 * swept along the group-39 thickness vector, UNION'd with the model box
 * `entityModelBounds` computes for the same entity.
 *
 * `null` only when the entity draws nothing AND has no model box (no
 * vertices).
 */
export function heavyPolylineDrawnBounds(entity: PolylineEntity): Aabb | null {
  // TWO DOCUMENTED EQUIVALENT MUTANTS (targeted mutation testing), both the
  // same shape — a guard an upstream contract already decides:
  //
  //   1. `model === ALWAYS_INCLUDE` → `false`. `entityModelBounds`'
  //      `"Polyline"` arm is `heavyPolylineBounds`, whose return type is
  //      `EntityViewBounds | null` — the never-drop sentinel is produced only
  //      by the BLOCK-reference arms (`blockInsertBounds`), which a POLYLINE
  //      can never take. Kept because the local `model` is typed
  //      `ResolvedEntityBounds` and narrowing it is what makes the object
  //      literal below type-check.
  //   2. `if (box)` → `true`. `boundsFromPoints` returns `null` only for an
  //      EMPTY point list, and `heavyPolylineDrawnRuns` never emits a run with
  //      no points (its own `indices.length === 0` guard skips it — pinned by
  //      `./heavyPolylineSelectDrawnShape.test.ts`'s "reports NO bounds at all
  //      for a polyline with no vertices"). Kept so a direct change to that
  //      function cannot turn this into `unionBounds(box, null)` and poison the
  //      box with `undefined`.
  const model = entityModelBounds(entity);
  const base =
    model === null || model === ALWAYS_INCLUDE
      ? null
      : { minX: model.minX, minY: model.minY, maxX: model.maxX, maxY: model.maxY };

  // THE WIDTH GATE (see the module doc for the measurements and the proof):
  // with no authored width there is no BAND, the drawn runs collapse to the
  // centreline `base` already bounds, and unioning them is a no-op that costs
  // 2.1x-4.1x the whole call. The predicate is `drawing-model`'s own
  // entity-level reading of groups 40 / 41 over the POLYLINE record's
  // defaults — the SAME rule `heavyPolylineDrawnRuns` applies per run — so the
  // branch cannot disagree with the walk it is skipping.
  //
  // DOCUMENTED EQUIVALENT MUTANT (targeted mutation testing):
  //   file:line        this file, the width gate
  //   mutator          ConditionalExpression → `false` (never take the gate)
  //   why equivalent   that mutant is EXACTLY the round-9 code: it runs the
  //                    drawn walk for a width-less entity too, and unions in a
  //                    box the model box already contains. Same answer, more
  //                    work — this gate buys TIME, not geometry, and a
  //                    behaviour test therefore cannot see it.
  //   the invariant    for a width-less entity every point of
  //                    `heavyPolylineDrawnRuns`, swept along the group-39
  //                    vector, lies inside `entityModelBounds`' box (the module
  //                    doc derives it per subtype and frame)
  //   proved by        `./heavyPolylineDrawnBounds.property.test.ts`'s P7,
  //                    which asserts that containment over generated
  //                    extrusions, bulges, thicknesses and closed flags
  // The OTHER direction is NOT equivalent and is killed: `→ true` (always take
  // the gate) and `BooleanLiteral → heavyPolylineDeclaresWidth(entity)`
  // (inverted) both drop the band, and both die on this file's own witnesses.
  if (!heavyPolylineDeclaresWidth(entity)) return base;

  let drawn: Aabb | null = null;
  for (const run of heavyPolylineDrawnRuns(entity)) {
    // `[p, p + thickness·N̂]` — the ends of the sweep, which bound all of it.
    const box = boundsFromPoints(
      withThicknessAlongExtrusion(run.points, entity.thickness, entity.extrusion),
    );
    if (box) drawn = drawn ? unionBounds(drawn, box) : box;
  }

  if (!drawn) return base;
  return base ? unionBounds(base, drawn) : drawn;
}
